/**
 * Hybridization RESULT interpretation (design D28/D29; av-methodology-hybridization
 * spec's interpretation requirement).
 *
 * `hybridization.ts` computes ONLY the resulting position (coluna/linha) — a
 * number, not a meaning. The author confirmed the app was missing exactly
 * this next layer: the thesis's own chain is
 *
 *   Conteúdo + Influências -> posição ponderada -> posição resultante -> INTERPRETAÇÃO
 *
 * and only the first three steps were implemented. This module is the
 * fourth: given the ORIGINAL position (the content's own) and the RESULT
 * position (`calculateHybridization()`'s output, resolved to a real
 * archetype/relevance pair), it returns what that result *means*.
 *
 * Design D29: reference-language identity (label, communicational profile)
 * is read exclusively from `reference-languages.json` via
 * `reference-language.ts` — never from `archetypes.json`'s own `.label` —
 * so this module is the interpretation layer D29 describes, structurally
 * separate from the matrix's own column/row identity. `archetypes.json`
 * (via `archetypes`/`order`) is still used here, but only for the
 * STRUCTURAL coluna→archetype lookup (design D2's numbering), the same
 * role `matrix.ts` uses it for — never for `.label`. `matrix.ts` must
 * never import this module or `reference-language.ts`.
 *
 * Two sources of interpretation, kept distinct and never merged:
 *
 * 1. `hybridizations.json`'s `documentedExamples` — thesis-attested text,
 *    `origin: "ferreira-2010"`. As of this writing there is exactly ONE
 *    entry (posição 10, Apêndice 4 item 8's "catálogo de produtos" worked
 *    example) — every other resulting position has none, and none may be
 *    added without new source grounding (CLAUDE.md).
 * 2. A GENERIC, template-driven statement computed from the result
 *    position's own reference-language entry (label, communication
 *    profile) — `origin: "derived-from-position"`, never presented as a
 *    historical citation. Critically, this is a TEMPLATE applied to
 *    whatever archetype pair actually occurred, not a lookup table of fixed
 *    (original, result) combinations — the author's explicit instruction:
 *    "não criar combinações fixas do tipo A+B=C."
 */

import { archetypes, hybridizations, methodology } from "./data-source";
import { classify } from "./classification";
import { activeProfileDimensions, referenceLanguageForArchetype } from "./reference-language";
import { getRecommendation, type RecommendationResult } from "./recommendation-engine";
import type { ArchetypeId, DocumentedHybridizationExample, MatrixPositionId } from "./types";

export const HYBRIDIZATION_INTERPRETATION_FALLBACK_ORIGIN = "derived-from-position" as const;

export interface HybridizationInterpretation {
  originalPosition: MatrixPositionId;
  resultPosition: MatrixPositionId;
  resultReferenceLanguageLabel: string;
  resultRelevanceLabel: string;
  /** Generic, template-driven statement — see module doc. Always present. */
  interpretation: string;
  interpretationOrigin: typeof HYBRIDIZATION_INTERPRETATION_FALLBACK_ORIGIN;
  /** The resulting position's communication profile, as recorded in
   *  reference-languages.json — not new prose, just surfaced for this
   *  context (design D29: the interpretation layer's own catalog, not
   *  archetypes.json). */
  extendedCharacteristics: string[];
  /** The one documented thesis example, only if the RESULT position matches
   *  it exactly — null for every other position. Never fabricated. */
  historicalExample: DocumentedHybridizationExample | null;
  /** The result position's own recorded recommendation (historical 2010 +
   *  current 2026, kept distinguishable) — reuses the same generic,
   *  position-driven machine every ordinary content position already goes
   *  through; nothing hybridization-specific is invented here. */
  recommendation: RecommendationResult;
}

const PROFILE_DIMENSION_LABELS: Record<string, string> = {
  text: "Texto",
  image: "Imagem",
  color: "Cor",
  sound: "Som",
  motion: "Imagem em movimento",
  interactivity: "Interatividade",
  mobility: "Mobilidade",
};

function referenceLanguageLabel(archetypeId: ArchetypeId): string {
  // Falls back to classify()'s archetype label only as a defensive measure
  // — every archetype has a reference-languages.json entry (data-shape
  // test asserts this), so this branch should be unreachable in practice.
  return referenceLanguageForArchetype(archetypeId)?.label ?? classify({ archetypeId, relevance: "low" }).archetype.label;
}

/**
 * Template sentence connecting the ORIGINAL reference language to the
 * RESULT reference language's characteristics — computed fresh for
 * whatever pair actually occurred, never a stored combination. Mirrors the
 * thesis's own phrasing pattern (Apêndice 4 item 8: "...embora queira
 * trabalhar com vídeos com linguagem de TV, ele poderá trabalhar com
 * algumas características de Outdoor") without repeating its specific
 * archetypes.
 */
function genericInterpretationStatement(
  originalLabel: string,
  originalArchetypeId: string,
  resultLabel: string,
  resultArchetypeId: string,
  resultRelevanceLabel: string,
): string {
  if (originalArchetypeId === resultArchetypeId) {
    return `A posição resultante do hibridismo permanece na linguagem de ${resultLabel}, com nível de relevância revisado para ${resultRelevanceLabel.toLowerCase()} — as influências reforçam a mesma linguagem em vez de deslocar o conteúdo para outra.`;
  }
  return `Incorporação de características de ${resultLabel} a conteúdo planejado com linguagem de ${originalLabel}.`;
}

/**
 * Interprets a hybridization result. Requires the CONTENT's own original
 * position and the RESULT position already resolved to a real
 * archetype/relevance pair (i.e. `calculateHybridization()`'s coluna/linha
 * already mapped back through archetypes.json/methodology.json — this
 * function does no averaging itself, per the author's "não altere a
 * fórmula já aprovada").
 */
export function interpretHybridizationResult(
  originalPosition: MatrixPositionId,
  resultPosition: MatrixPositionId,
): HybridizationInterpretation {
  const resultRelevanceLevel = classify(resultPosition).relevanceLevel;
  const originalLabel = referenceLanguageLabel(originalPosition.archetypeId);
  const resultLabel = referenceLanguageLabel(resultPosition.archetypeId);
  const resultEntry = referenceLanguageForArchetype(resultPosition.archetypeId);

  const historicalExample =
    hybridizations.documentedExamples.find(
      (example) => example.archetypeId === resultPosition.archetypeId && example.relevance === resultPosition.relevance,
    ) ?? null;

  return {
    originalPosition,
    resultPosition,
    resultReferenceLanguageLabel: resultLabel,
    resultRelevanceLabel: resultRelevanceLevel.label,
    interpretation: genericInterpretationStatement(
      originalLabel,
      originalPosition.archetypeId,
      resultLabel,
      resultPosition.archetypeId,
      resultRelevanceLevel.label,
    ),
    interpretationOrigin: HYBRIDIZATION_INTERPRETATION_FALLBACK_ORIGIN,
    extendedCharacteristics: resultEntry
      ? activeProfileDimensions(resultEntry).map((dimension) => PROFILE_DIMENSION_LABELS[dimension] ?? dimension)
      : [],
    historicalExample,
    recommendation: getRecommendation(resultPosition),
  };
}

/**
 * Resolves a 1-indexed (coluna, linha) pair — calculateHybridization()'s
 * raw output — back to a real {archetypeId, relevance} position, or null
 * if out of range (defensive; coluna/linha are always in-range by
 * construction, since the formula's inputs are themselves valid archetype
 * orders / relevance values, but this stays explicit rather than assuming
 * it). Column resolution goes through `archetypes` (`order` — structural,
 * design D2), the same source `matrix.ts` uses for column layout; never
 * through reference-languages.json, which has no independent notion of
 * column order beyond mirroring it.
 */
export function resolveHybridizationPosition(coluna: number, linha: number): MatrixPositionId | null {
  const archetype = archetypes.find((a) => a.order === coluna);
  const relevanceLevel = methodology.dimensions.relevance.levels.find((l) => l.value === linha);
  if (!archetype || !relevanceLevel) return null;
  return { archetypeId: archetype.id, relevance: relevanceLevel.id };
}
